import { GrowthBook } from '@growthbook/growthbook-react';
import { getGrowthBookConfig } from '@constants/growthBook';
import { getEnvVar } from 'utils/env';

const config = getGrowthBookConfig();

/**
 * Bounds how long a gated first render waits on a slow or dead GrowthBook.
 * Generous on purpose: expiring isn't a failure, but it does mean the flag
 * arrives later and visibly flips the value the first render used.
 */
const INIT_TIMEOUT_MS = 5_000;

/**
 * The app's single GrowthBook instance.
 *
 * Environments with no GrowthBook host (gov) still get an instance; it is
 * initialized with an empty payload instead of loading, so every flag reads
 * false while `ready` still becomes true. That keeps the provider, the read
 * path, and readiness gating branch-free.
 */
export const growthBookClient = new GrowthBook({
  ...(config ? { apiHost: config.apiHost, clientKey: config.clientKey } : {}),
  disableCache: true,
});

let initPromise: Promise<unknown> | undefined;

/**
 * Applies the empty payload `init` would have applied itself. `init` ends in
 * `setPayload` — the only thing that flips `ready` — on success, on network
 * failure, and on timeout, but a rejection skips it, and callers gating on
 * readiness would then wait forever.
 */
const markReadyWithNoFlags = () =>
  growthBookClient.setPayload({}).catch((error) => {
    // Nothing else flips `ready`, so this is the one failure that leaves a
    // gated caller waiting forever.
    console.error('GrowthBook could not be marked ready; flag gates will not open.', error);
  });

/**
 * Idempotent: React may mount the provider twice (StrictMode), and a second
 * init would otherwise refetch. Note the client is never destroyed — its
 * lifetime is the page's, and destroying a module-level singleton on unmount
 * would leave a later init running against a dead instance.
 *
 * Resolves, and leaves `ready` true, on every outcome. An empty payload reads
 * as every flag off, which is the fallback we want when flags can't be loaded.
 */
export const initGrowthBookClient = () => {
  if (initPromise) return initPromise;

  // Environments with no GrowthBook configuration (gov today). Applying an
  // empty payload marks the client ready with every flag off; loading would
  // only reject on the missing client key. The warning is the signal that an
  // environment expected to have flags silently has none.
  //
  // `setPayload`, not `initSync`: only the former re-renders the provider's
  // subtree, and a consumer already mounted would otherwise never see `ready`
  // turn true.
  if (!config) {
    console.warn(
      `GrowthBook is not configured for UI_ENV "${getEnvVar('UI_ENV') ?? 'unset'}" — every feature flag will read false.`,
    );
    initPromise = markReadyWithNoFlags();
    return initPromise;
  }

  initPromise = growthBookClient
    .init({ streaming: true, timeout: INIT_TIMEOUT_MS })
    .catch((error) => {
      // Falling back to no flags is correct, but it is indistinguishable from
      // every flag being switched off — so a failed load has to say so, or a
      // GrowthBook outage looks exactly like a deliberate rollback.
      console.error('GrowthBook failed to load feature flags; all flags read false.', error);

      return markReadyWithNoFlags();
    });

  return initPromise;
};
